/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, ReactNode, useState } from "react"
import { CheckIcon } from "@heroicons/react/solid"
import { classNames } from "@medaica/common/services/util"
import { WizardConsents } from "../stores/wizard-store"
import { bookingColors } from "../booking-theme"

// `action` (a control beside the text) and `expansion` (a panel below the row) both render
// OUTSIDE the <label>: a <label> may not contain a labelable element other than its own
// checkbox, and a click inside either must not toggle it. Used by "View full form".
type ConsentItem = {
  key: keyof WizardConsents
  title: string
  body: ReactNode
  action?: ReactNode
  expansion?: ReactNode
}

/**
 * One consent row with a custom checkbox (05 §4 redesign): a real (screen-reader-visible) checkbox
 * is kept for a11y + the exact same onChange, but visually hidden and replaced by a larger,
 * brand-blue box with a check icon. Checked/focus visuals are React-driven (Tailwind 2 AOT here
 * doesn't generate peer-checked/focus-within), so no native checkbox styling shows.
 */
const ConsentRow = ({
  item,
  checked,
  onToggle,
}: {
  item: ConsentItem
  checked: boolean
  onToggle: (key: keyof WizardConsents, checked: boolean) => void
}): ReactElement => {
  const [focused, setFocused] = useState(false)
  // Two labels for one checkbox (both legal, both toggle it): the box nests the input,
  // the text uses htmlFor. Splitting them keeps `action` a SIBLING of the text label
  // rather than a labelable element inside it. The box contributes no accessible name
  // (its only child is aria-hidden), so the name stays "<title> <body>".
  const inputId = `consent-${item.key}`
  return (
    <div>
      <div className="flex items-start gap-3">
        <label className="relative mt-0.5 flex-shrink-0 cursor-pointer">
          <input
            id={inputId}
            type="checkbox"
            className="sr-only"
            checked={checked}
            onChange={(event) => onToggle(item.key, event.target.checked)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
          <span
            aria-hidden="true"
            className={classNames(
              "flex h-5 w-5 items-center justify-center rounded-md border-2 transition-colors",
              checked ? "border-transparent" : "border-gray-300 bg-white",
              focused ? "ring-2 ring-primary-300 ring-offset-1" : ""
            )}
            style={checked ? { backgroundColor: bookingColors.primary, borderColor: bookingColors.primary } : undefined}
          >
            {checked && <CheckIcon className="h-3.5 w-3.5 text-white" />}
          </span>
        </label>
        <span className="text-sm text-gray-700">
          <label htmlFor={inputId} className="cursor-pointer">
            <span className="font-semibold text-gray-900">{item.title}</span> {item.body}
          </label>
          {item.action && <> {item.action}</>}
        </span>
      </div>
      {item.expansion}
    </div>
  )
}

const ConsentList = ({
  items,
  values,
  onToggle,
}: {
  items: ConsentItem[]
  values: WizardConsents
  onToggle: (key: keyof WizardConsents, checked: boolean) => void
}): ReactElement => (
  <div className="space-y-4">
    {items.map((item) => (
      <ConsentRow key={item.key} item={item} checked={values[item.key]} onToggle={onToggle} />
    ))}
  </div>
)

export type { ConsentItem }
export default ConsentList
